import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import CommonWrapper from '../../components/ui_components/CommonComponentsStructureCss';
import { useAppSelector } from '../../store/hooks';
import { apiGetLeaderboard, apiGetLeaderboardByExpansion } from '../../api/userCardApi';
import type { LeaderboardEntry } from '../../api/userCardApi';
import { apiGetExpansions } from '../../api/expansionApi';
import type { Expansion } from '../../types/models/expansion';

// ─── Styled ───────────────────────────────────────────────────────────────────

const PageInner = styled.div`
  padding: 40px 24px;
  max-width: 860px;
  margin: 0 auto;
`;

const PageTitle = styled.h1`
  font-size: 26px;
  font-weight: 700;
  color: gold;
  margin: 0 0 4px;
`;

const PageSubtitle = styled.p`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.4);
  margin: 0 0 16px;
`;

const InfoBanner = styled.div`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.45);
  background-color: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-left: 3px solid rgba(255, 215, 0, 0.5);
  border-radius: 6px;
  padding: 10px 14px;
  margin-bottom: 36px;
  line-height: 1.6;
`;

// ─── Podio ────────────────────────────────────────────────────────────────────

const PODIUM_ACCENT = {
  1: { color: '#FFD700', rgb: '255,215,0',   baseH: 120, width: 230 },
  2: { color: '#C0C0C0', rgb: '192,192,192', baseH: 80,  width: 200 },
  3: { color: '#cd7f32', rgb: '205,127,50',  baseH: 52,  width: 186 },
} as const;

const Podium = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-end;
  gap: 0;
  margin-bottom: 56px;
`;

const PodiumColumn = styled.div<{ $rank: 1|2|3 }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: ${({ $rank }) => PODIUM_ACCENT[$rank].width}px;
  cursor: pointer;
  transition: transform 0.22s ease;
  &:hover { transform: translateY(-6px); }
`;

/* Card galleggiante sopra il gradino */
const PodiumCard = styled.div<{ $rank: 1|2|3 }>`
  width: calc(100% - 16px);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ $rank }) => $rank === 1 ? '8px' : '6px'};
  padding: ${({ $rank }) => $rank === 1 ? '24px 16px 20px' : '18px 12px 16px'};
  background: linear-gradient(
    160deg,
    rgba(${({ $rank }) => PODIUM_ACCENT[$rank].rgb}, 0.1) 0%,
    rgba(0,0,0,0.4) 100%
  );
  border: 1px solid rgba(${({ $rank }) => PODIUM_ACCENT[$rank].rgb}, 0.3);
  border-radius: 14px;
  margin-bottom: -1px;
  box-shadow:
    0 0 0 1px rgba(0,0,0,0.6),
    ${({ $rank }) => $rank === 1
      ? '0 -8px 32px rgba(255,215,0,0.18), 0 8px 24px rgba(0,0,0,0.5)'
      : '0 4px 20px rgba(0,0,0,0.4)'};
  backdrop-filter: blur(2px);
  position: relative;
  z-index: 1;
`;

/* Numero rank in Oswald — grande e prominente */
const RankNumber = styled.div<{ $rank: 1|2|3 }>`
  font-family: 'Oswald', sans-serif;
  font-size: ${({ $rank }) => $rank === 1 ? '36px' : '26px'};
  font-weight: 700;
  line-height: 1;
  color: ${({ $rank }) => PODIUM_ACCENT[$rank].color};
  text-shadow: 0 0 ${({ $rank }) => $rank === 1 ? '20px' : '10px'} rgba(${({ $rank }) => PODIUM_ACCENT[$rank].rgb}, 0.5);
  letter-spacing: -1px;
`;

const PodiumName = styled.div<{ $rank: 1|2|3 }>`
  font-family: 'Inter', sans-serif;
  font-size: ${({ $rank }) => $rank === 1 ? '15px' : '13px'};
  font-weight: 600;
  color: #fff;
  text-align: center;
  word-break: break-word;
  line-height: 1.3;
`;

const PodiumNick = styled.div`
  font-family: 'Inter', sans-serif;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.35);
  text-align: center;
`;

const PodiumScore = styled.div<{ $rank: 1|2|3 }>`
  font-family: 'Oswald', sans-serif;
  font-size: ${({ $rank }) => $rank === 1 ? '30px' : '22px'};
  font-weight: 600;
  color: ${({ $rank }) => PODIUM_ACCENT[$rank].color};
  line-height: 1;
  letter-spacing: 0.5px;
`;

const PodiumScoreLabel = styled.div`
  font-size: 10px;
  color: rgba(255, 255, 255, 0.3);
  text-transform: uppercase;
  letter-spacing: 0.1em;
  margin-top: -2px;
`;

const PodiumUniqueCards = styled.div`
  font-size: 11px;
  color: rgba(255, 255, 255, 0.35);
  margin-top: 2px;
`;

/* Gradino fisico del podio */
const PodiumBase = styled.div<{ $rank: 1|2|3 }>`
  width: 100%;
  height: ${({ $rank }) => PODIUM_ACCENT[$rank].baseH}px;
  background: linear-gradient(
    180deg,
    rgba(${({ $rank }) => PODIUM_ACCENT[$rank].rgb}, 0.15) 0%,
    rgba(${({ $rank }) => PODIUM_ACCENT[$rank].rgb}, 0.04) 100%
  );
  border: 1px solid rgba(${({ $rank }) => PODIUM_ACCENT[$rank].rgb}, 0.2);
  border-top: none;
  border-radius: 0 0 4px 4px;
`;

// ─── Avatar stack ─────────────────────────────────────────────────────────────

const AvatarStack = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const AvatarCircle = styled.div<{ $size: number; $offset: number }>`
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  border-radius: 50%;
  border: 2px solid #111;
  overflow: hidden;
  background-color: rgba(255, 255, 255, 0.1);
  flex-shrink: 0;
  margin-left: ${({ $offset }) => $offset}px;

  &:first-child { margin-left: 0; }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: top;
  }
`;

const AvatarMore = styled.div<{ $size: number }>`
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  border-radius: 50%;
  border: 2px solid #111;
  background-color: rgba(255, 255, 255, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${({ $size }) => Math.round($size * 0.32)}px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.45);
  flex-shrink: 0;
  margin-left: ${({ $size }) => -Math.round($size * 0.28)}px;
`;

// ─── Lista ────────────────────────────────────────────────────────────────────

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  text-align: left;
  padding: 10px 14px;
  font-size: 11px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.35);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
`;

const ThRight = styled(Th)`
  text-align: right;
`;

const Tr = styled.tr<{ $isMe: boolean }>`
  background-color: ${({ $isMe }) => ($isMe ? 'rgba(255,215,0,0.06)' : 'transparent')};
  cursor: pointer;
  &:hover td { background-color: rgba(255,255,255,0.05); }
`;

const Td = styled.td`
  padding: 12px 14px;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.8);
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  vertical-align: middle;
`;

const TdRight = styled(Td)`
  text-align: right;
`;

const RankCell = styled(Td)`
  font-weight: 700;
  color: rgba(255, 255, 255, 0.35);
  width: 48px;
`;

const Username = styled.span`
  font-weight: 600;
  color: #fff;
`;

const Nickname = styled.span`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.35);
  margin-left: 6px;
`;


const MeTag = styled.span`
  font-size: 10px;
  background-color: rgba(255, 215, 0, 0.15);
  color: gold;
  border: 1px solid rgba(255, 215, 0, 0.3);
  border-radius: 4px;
  padding: 1px 5px;
  margin-left: 6px;
  font-weight: 600;
  vertical-align: middle;
`;

const StatusText = styled.p`
  text-align: center;
  color: rgba(255, 255, 255, 0.4);
  margin-top: 48px;
`;

const RarityLegend = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 28px;
`;

const RarityLegendItem = styled.div<{ $color: string }>`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
`;

const RarityLegendDot = styled.span<{ $color: string }>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: ${({ $color }) => $color};
  flex-shrink: 0;
`;

const RarityRow = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  flex-wrap: wrap;
  justify-content: center;
`;

const RarityBadge = styled.span<{ $color: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 4px;
  border-radius: 10px;
  border: 1.5px solid ${({ $color }) => $color};
  color: ${({ $color }) => $color};
  font-size: 10px;
  font-weight: 700;
  line-height: 1;
  vertical-align: middle;
`;

// ─── Filtro espansione (custom dropdown) ─────────────────────────────────────

const DropdownWrapper = styled.div`
  position: relative;
  margin-bottom: 28px;
`;

const DropdownTrigger = styled.button<{ $open: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  background-color: rgba(255, 255, 255, 0.06);
  border: 1px solid ${({ $open }) => $open ? 'rgba(255,215,0,0.6)' : 'rgba(255,255,255,0.14)'};
  border-radius: 10px;
  color: #fff;
  font-size: 14px;
  font-weight: 500;
  padding: 10px 14px;
  cursor: pointer;
  outline: none;
  min-width: 240px;
  transition: border-color 0.18s;
  &:hover { border-color: rgba(255,215,0,0.4); }
`;

const TriggerLabel = styled.span`
  flex: 1;
  text-align: left;
`;

const TriggerChevron = styled.span<{ $open: boolean }>`
  font-size: 10px;
  color: rgba(255,255,255,0.4);
  transform: ${({ $open }) => $open ? 'rotate(180deg)' : 'rotate(0)'};
  transition: transform 0.18s;
`;

const TriggerThumb = styled.img`
  width: 26px;
  height: 26px;
  border-radius: 5px;
  object-fit: cover;
  flex-shrink: 0;
`;

const DropdownPanel = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  min-width: 100%;
  max-width: 380px;
  max-height: 300px;
  overflow-y: auto;
  background-color: #1c1c1c;
  border: 1px solid rgba(255,255,255,0.12);
  border-radius: 10px;
  z-index: 100;
  box-shadow: 0 8px 32px rgba(0,0,0,0.6);
  padding: 6px;
`;

const DropdownOption = styled.div<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 12px;
  border-radius: 7px;
  cursor: pointer;
  font-size: 13px;
  font-weight: ${({ $active }) => $active ? '600' : '400'};
  color: ${({ $active }) => $active ? '#fff' : 'rgba(255,255,255,0.7)'};
  background-color: ${({ $active }) => $active ? 'rgba(255,215,0,0.1)' : 'transparent'};
  &:hover { background-color: rgba(255,255,255,0.08); }
`;

const OptionThumb = styled.img`
  width: 34px;
  height: 34px;
  border-radius: 6px;
  object-fit: cover;
  flex-shrink: 0;
  border: 1px solid rgba(255,255,255,0.1);
`;

const OptionThumbPlaceholder = styled.div`
  width: 34px;
  height: 34px;
  border-radius: 6px;
  background-color: rgba(255,215,0,0.08);
  border: 1px solid rgba(255,215,0,0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  flex-shrink: 0;
`;

// ─── WrestlerAvatars ──────────────────────────────────────────────────────────

const MAX_VISIBLE = 5;

const WrestlerAvatars: React.FC<{
  wrestlers: LeaderboardEntry['wrestlers'];
  size?: number;
}> = ({ wrestlers, size = 28 }) => {
  if (!wrestlers || wrestlers.length === 0) return null;
  const visible = wrestlers.slice(0, MAX_VISIBLE);
  const extra = wrestlers.length - MAX_VISIBLE;
  const offset = -Math.round(size * 0.28);
  return (
    <AvatarStack>
      {visible.map((w) => (
        <AvatarCircle key={w.id} $size={size} $offset={offset}>
          {w.avatar_path ? (
            <img
              src={w.avatar_path.startsWith('http') ? w.avatar_path : process.env.REACT_APP_STORAGE_URL + w.avatar_path}
              alt={w.name}
              title={w.name}
            />
          ) : null}
        </AvatarCircle>
      ))}
      {extra > 0 && <AvatarMore $size={size}>+{extra}</AvatarMore>}
    </AvatarStack>
  );
};

// ─── RarityBreakdown ──────────────────────────────────────────────────────────

const RarityBreakdown: React.FC<{ entry: LeaderboardEntry; center?: boolean }> = ({ entry, center }) => (
  <RarityRow style={{ justifyContent: center ? 'center' : 'flex-start' }}>
    {entry.legendary_count > 0 && <RarityBadge $color="gold">{entry.legendary_count}</RarityBadge>}
    {entry.epic_count > 0 && <RarityBadge $color="#b04df0">{entry.epic_count}</RarityBadge>}
    {entry.rare_count > 0 && <RarityBadge $color="#4da6ff">{entry.rare_count}</RarityBadge>}
    {entry.common_count > 0 && <RarityBadge $color="rgba(255,255,255,0.5)">{entry.common_count}</RarityBadge>}
  </RarityRow>
);

// ─── ExpansionFilter ──────────────────────────────────────────────────────────

type FilterId = 'global' | number;

const getImgSrc = (path: string | null): string | null => {
  if (!path) return null;
  return path.startsWith('http') ? path : (process.env.REACT_APP_STORAGE_URL ?? '') + path;
};

const ExpansionFilter: React.FC<{
  expansions: Expansion[];
  selectedId: FilterId;
  onChange: (id: FilterId) => void;
}> = ({ expansions, selectedId, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const selectedExp = selectedId !== 'global' ? expansions.find((e) => e.id === selectedId) ?? null : null;
  const triggerImgSrc = selectedExp ? getImgSrc(selectedExp.expansion_path) : null;

  return (
    <DropdownWrapper ref={wrapperRef}>
      <DropdownTrigger $open={isOpen} onClick={() => setIsOpen((o) => !o)}>
        {triggerImgSrc
          ? <TriggerThumb src={triggerImgSrc} alt={selectedExp!.name} />
          : <span style={{ fontSize: 18, lineHeight: 1 }}>🏆</span>}
        <TriggerLabel>{selectedId === 'global' ? 'Classifica Globale' : (selectedExp?.name ?? '...')}</TriggerLabel>
        <TriggerChevron $open={isOpen}>▼</TriggerChevron>
      </DropdownTrigger>

      {isOpen && (
        <DropdownPanel>
          <DropdownOption $active={selectedId === 'global'} onClick={() => { onChange('global'); setIsOpen(false); }}>
            <OptionThumbPlaceholder>🏆</OptionThumbPlaceholder>
            Classifica Globale
          </DropdownOption>
          {expansions.map((exp) => {
            const imgSrc = getImgSrc(exp.expansion_path);
            return (
              <DropdownOption key={exp.id} $active={selectedId === exp.id} onClick={() => { onChange(exp.id); setIsOpen(false); }}>
                {imgSrc
                  ? <OptionThumb src={imgSrc} alt={exp.name} />
                  : <OptionThumbPlaceholder>{exp.name[0]}</OptionThumbPlaceholder>}
                {exp.name}
              </DropdownOption>
            );
          })}
        </DropdownPanel>
      )}
    </DropdownWrapper>
  );
};

// ─── Component ────────────────────────────────────────────────────────────────

const LeaderboardPage: React.FC = () => {
  const [entries, setEntries] = useState<LeaderboardEntry[]>([]);
  const [expansions, setExpansions] = useState<Expansion[]>([]);
  const [selectedId, setSelectedId] = useState<FilterId>('global');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const cache = useRef(new Map<FilterId, LeaderboardEntry[]>());

  const navigate = useNavigate();
  const currentUsername = useAppSelector((state) => state.auth.user?.username ?? state.auth.userLoggedState?.username ?? null);

  useEffect(() => {
    let isMounted = true;
    Promise.all([
      apiGetLeaderboard().then((res) => Array.isArray(res) ? res : (res?.data ?? [])),
      apiGetExpansions(),
    ])
      .then(([leaderboardData, expansionList]) => {
        if (!isMounted) return;
        cache.current.set('global', leaderboardData);
        setEntries(leaderboardData);
        setExpansions(expansionList);
      })
      .catch(() => { if (isMounted) setError('Errore durante il caricamento della classifica.'); })
      .finally(() => { if (isMounted) setIsLoading(false); });
    return () => { isMounted = false; };
  }, []);

  const handleFilterChange = async (id: FilterId) => {
    if (id === selectedId) return;
    setSelectedId(id);
    if (cache.current.has(id)) {
      setEntries(cache.current.get(id)!);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const res = id === 'global'
        ? await apiGetLeaderboard()
        : await apiGetLeaderboardByExpansion(id as number);
      const data = Array.isArray(res) ? res : (res?.data ?? []);
      cache.current.set(id, data);
      setEntries(data);
    } catch {
      setError('Errore durante il caricamento della classifica.');
    } finally {
      setIsLoading(false);
    }
  };

  const top3 = entries.slice(0, 3);
  const rest = entries.slice(3);

  return (
    <CommonWrapper>
      <PageInner>
        <PageTitle>Classifica Collezione</PageTitle>
        <PageSubtitle>
          {selectedId === 'global'
            ? 'I migliori collezionisti GWF per valore album'
            : `I migliori collezionisti di ${expansions.find((e) => e.id === selectedId)?.name ?? '...'}`}
        </PageSubtitle>

        <InfoBanner>
          La classifica è basata sul <strong style={{ color: 'rgba(255,255,255,0.7)' }}>valore ponderato</strong> della collezione, non sul numero di carte.
          Ogni rarità vale di più della precedente.
          I doppioni contribuiscono in misura <strong style={{ color: 'rgba(255,255,255,0.7)' }}>ridotta</strong> rispetto alle carte singole.
        </InfoBanner>

        {isLoading && <StatusText>Caricamento...</StatusText>}
        {error && <StatusText style={{ color: '#dc3545' }}>{error}</StatusText>}

        {!isLoading && !error && entries.length === 0 && (
          <StatusText>Nessun collezionista ancora.</StatusText>
        )}

        {!isLoading && !error && entries.length > 0 && (
          <>
            {/* Legenda rarità */}
            <RarityLegend>
              <RarityLegendItem $color="rgba(255,255,255,0.45)">
                <RarityLegendDot $color="rgba(255,255,255,0.45)" />
                Common
              </RarityLegendItem>
              <RarityLegendItem $color="rgba(255,255,255,0.5)">
                <RarityLegendDot $color="#4da6ff" />
                Rare
              </RarityLegendItem>
              <RarityLegendItem $color="rgba(255,255,255,0.5)">
                <RarityLegendDot $color="#b04df0" />
                Epic
              </RarityLegendItem>
              <RarityLegendItem $color="rgba(255,255,255,0.5)">
                <RarityLegendDot $color="gold" />
                Legendary
              </RarityLegendItem>
            </RarityLegend>

            {expansions.length > 0 && (
              <ExpansionFilter
                expansions={expansions}
                selectedId={selectedId}
                onChange={handleFilterChange}
              />
            )}

            {/* Podio top 3 */}
            {top3.length > 0 && (
              <Podium>
                {([top3[1], top3[0], top3[2]] as const).map((entry, i) => {
                  if (!entry) return null;
                  const rank = ([2, 1, 3] as const)[i] as 1 | 2 | 3;
                  const isMe = entry.username === currentUsername;
                  return (
                    <PodiumColumn key={entry.username} $rank={rank} onClick={() => navigate(`/carte/utente/${entry.username}`)}>
                      <PodiumCard $rank={rank}>
                        <RankNumber $rank={rank}>{rank}</RankNumber>
                        <WrestlerAvatars wrestlers={entry.wrestlers} size={rank === 1 ? 36 : 28} />
                        <PodiumName $rank={rank}>
                          {entry.username}
                          {isMe && <MeTag>Tu</MeTag>}
                        </PodiumName>
                        {entry.nickname_forum && <PodiumNick>{entry.nickname_forum}</PodiumNick>}
                        <PodiumScore $rank={rank}>{entry.score}</PodiumScore>
                        <PodiumScoreLabel>punti</PodiumScoreLabel>
                        <PodiumUniqueCards>{entry.unique_cards} carte uniche</PodiumUniqueCards>
                        <RarityBreakdown entry={entry} center />
                      </PodiumCard>
                      <PodiumBase $rank={rank} />
                    </PodiumColumn>
                  );
                })}
              </Podium>
            )}

            {/* Lista completa dal 4° in poi */}
            {rest.length > 0 && (
              <Table>
                <thead>
                  <tr>
                    <Th>#</Th>
                    <Th>Utente</Th>
                    <ThRight>Punteggio</ThRight>
                    <ThRight>Rarità</ThRight>
                    <ThRight>Copie tot.</ThRight>
                  </tr>
                </thead>
                <tbody>
                  {rest.map((entry) => {
                    const isMe = entry.username === currentUsername;
                    return (
                      <Tr key={entry.username} $isMe={isMe} onClick={() => navigate(`/carte/utente/${entry.username}`)}>
                        <RankCell>#{entry.rank}</RankCell>
                        <Td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <WrestlerAvatars wrestlers={entry.wrestlers} size={26} />
                            <span>
                              <Username>{entry.username}</Username>
                              {entry.nickname_forum && <Nickname>{entry.nickname_forum}</Nickname>}
                              {isMe && <MeTag>Tu</MeTag>}
                            </span>
                          </div>
                        </Td>
                        <TdRight>
                          <span style={{ fontWeight: 700, color: '#fff' }}>{entry.score}</span>
                          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)', marginTop: 2 }}>{entry.unique_cards} uniche</div>
                        </TdRight>
                        <TdRight><RarityBreakdown entry={entry} /></TdRight>
                        <TdRight>{entry.total_copies}</TdRight>
                      </Tr>
                    );
                  })}
                </tbody>
              </Table>
            )}
          </>
        )}
      </PageInner>
    </CommonWrapper>
  );
};

export default LeaderboardPage;
